/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  static boundAttr = 'data-td-facets-toggle-bound';

  constructor() {
    FacetsToggle.init();

    // Defer non-critical resize work to avoid competing with early interactions.
    const dispatchResize = () => window.dispatchEvent(new Event('resize.resize-select'));
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(dispatchResize, { timeout: 1000 });
    } else {
      setTimeout(dispatchResize, 250);
    }
  }

  /**
   * Bind the facet drawer toggle click handler if the button exists and isn't already bound.
   * This must be resilient because the filter UI can be re-rendered via AJAX (replacing the button node).
   */
  static init() {
    const container = document.getElementById('Facet-Drawer');
    const button = document.getElementById('Facets-Toggle');
    if (!container || !button) return;

    if (button.hasAttribute(FacetsToggle.boundAttr)) return;
    button.setAttribute(FacetsToggle.boundAttr, 'true');

    button.addEventListener('click', (e) => {
      e.preventDefault();
      // Prevent heavy document-level click handlers (e.g. jQuery) from running for this interaction.
      e.stopPropagation();

      // Keep the click handler lightweight to reduce INP; do DOM/state work in the next task.
      setTimeout(() => {
        const isActive = container.classList.contains('active');
        if (isActive) {
          container.classList.remove('active');
          // Only remove click-capture state if no other side panel is active.
          if (!document.querySelector('.side-panel.active')) {
            document.body.classList.remove('open-cc');
          }
          return;
        }

        document.body.classList.add('open-cc');
        container.classList.add('active');
      }, 0);
    });
  }
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFacetInteractionCapture = this.onFacetInteractionCapture.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);
    this.onFormInput = this.debouncedOnSubmit.bind(this);
    this._listenersInitialized = false;
  }

  static abortController = null;
  static domParser = new DOMParser();
  static listenersInitialized = false;

  /**
   * Schedule non-critical DOM work so it doesn't compete with interaction responsiveness (INP).
   * @param {Function} fn
   */
  static scheduleNonCriticalWork(fn) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => fn(), { timeout: 1500 });
      return;
    }
    setTimeout(fn, 50);
  }

  /**
   * Replace `target` element's children with a clone of `source` element's children.
   * This avoids reparsing HTML strings (innerHTML/outerHTML) since `source` is already parsed.
   * @param {Element|null} target
   * @param {Element|null} source
   */
  static replaceChildrenFromElement(target, source) {
    if (!target || !source) return;
    const clone = source.cloneNode(true);
    target.replaceChildren(...clone.childNodes);
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  /**
   * Set up event listeners for this instance.
   * NOTE: Called from connectedCallback to avoid doing work during element construction.
   */
  setupEventListeners() {
    if (this._listenersInitialized) return;
    const form = this.querySelector('form');
    if (!form) return;
    form.addEventListener('input', this.onFormInput);

    // Reduce INP by preventing expensive document-level click handlers (often from apps) from running
    // for facet interactions. This keeps default input behavior intact while stopping bubbling.
    //
    // IMPORTANT: Do NOT use capture-phase stopPropagation here — it can prevent events from reaching
    // the target (breaking the drawer toggle and delegated handlers like facet-remove).
    this.addEventListener('click', this.onFacetInteractionCapture);

    this._listenersInitialized = true;
  }

  onFacetInteractionCapture(event) {
    // Only stop propagation for interactions within the facet UI (inputs/labels/summaries/links).
    const interactive = event.target.closest('input, label, summary, a, button');
    if (!interactive) return;
    event.stopPropagation();
  }

  static setListeners() {
    if (FacetFiltersForm.listenersInitialized) return;
    FacetFiltersForm.listenersInitialized = true;

    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    // Handle facet remove actions via events (avoid direct cross-component calls).
    document.addEventListener('td:facet-remove', (event) => {
      const href = event?.detail?.href;
      if (!href) return;
      const url = href.indexOf('?') === -1 ? '' : href.slice(href.indexOf('?') + 1);

      // Keep the interaction responsive (INP): defer the heavier work to the next task.
      setTimeout(() => {
        FacetFiltersForm.toggleActiveFacets();
        FacetFiltersForm.renderPage(url);
      }, 0);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    // Cancel any in-flight requests from previous interactions to avoid stale renders and reduce main-thread work.
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();

    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }


    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

  }

  static renderSectionFromFetch(url, event) {
    const controller = FacetFiltersForm.abortController;
    const signal = controller ? controller.signal : undefined;

    fetch(url, {
        signal
      })
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // If this request was aborted due to a newer interaction, ignore the response.
        if (signal?.aborted) return;

        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, {
          html,
          url
        }];

        // Defer parsing + DOM updates to the next task so the browser can paint between async steps.
        setTimeout(() => {
          if (signal?.aborted) return;

          // Parse once per response and reuse across render steps.
          const parsedHTML = FacetFiltersForm.domParser.parseFromString(html, 'text/html');
          FacetFiltersForm.renderProductGridContainer(parsedHTML);
          FacetFiltersForm.renderProductCount(parsedHTML);

          // Filter UI updates are heavier than grid/count; defer to keep INP low.
          FacetFiltersForm.scheduleNonCriticalWork(() => {
            FacetFiltersForm.renderFilters(parsedHTML, event);
          });
        }, 0);
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        throw error;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    // Defer cached renders as well to keep interactions snappy.
    setTimeout(() => {
      const parsedHTML = FacetFiltersForm.domParser.parseFromString(html, 'text/html');
      FacetFiltersForm.renderProductGridContainer(parsedHTML);
      FacetFiltersForm.renderProductCount(parsedHTML);
      FacetFiltersForm.scheduleNonCriticalWork(() => {
        FacetFiltersForm.renderFilters(parsedHTML, event);
      });
    }, 0);
  }

  static renderProductGridContainer(parsedHTML) {
    // Only update the product grid + pagination to reduce DOM churn (and improve INP) versus replacing
    // the entire #ProductGridContainer, which also contains the filter UI markup.
    const sourceGrid = parsedHTML.getElementById('product-grid');
    const targetGrid = document.getElementById('product-grid');

    if (sourceGrid && targetGrid) {
      FacetFiltersForm.replaceChildrenFromElement(targetGrid, sourceGrid);
      targetGrid.classList.remove('loading');
    }

    const sourcePagination = parsedHTML.querySelector('pagination-theme.pagination');
    const targetPagination = document.querySelector('pagination-theme.pagination');
    if (sourcePagination && targetPagination) {
      targetPagination.replaceWith(sourcePagination.cloneNode(true));
    } else if (!sourcePagination && targetPagination) {
      targetPagination.remove();
    } else if (sourcePagination && !targetPagination) {
      // If pagination appears after filtering, insert it after the product grid.
      targetGrid?.after(sourcePagination.cloneNode(true));
    }
  }

  static renderProductCount(parsedHTML) {
    const countHtml = parsedHTML.getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        FacetFiltersForm.replaceChildrenFromElement(item, countHtml);
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      const target = document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`);
      FacetFiltersForm.replaceChildrenFromElement(target, element);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));

    // Safe rebind in case the facets toggle exists within any replaced filter markup.
    if (typeof FacetsToggle !== 'undefined') {
      FacetsToggle.init();
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      FacetFiltersForm.replaceChildrenFromElement(target, activeFacetsElement);
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      FacetFiltersForm.replaceChildrenFromElement(target, source);
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      targetElement.replaceWith(sourceElement.cloneNode(true));
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
    {
      section: document.getElementById('product-grid').dataset.id,
    }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const formData = new FormData(event.target.closest('form'));
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this._listenersInitialized = false;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    if (this._listenersInitialized) return;
    this._listenersInitialized = true;
    // Use event delegation so this keeps working even if inner markup is re-rendered.
    this.addEventListener('click', this.onClick);
  }

  onClick(event) {
    const link = event.target.closest('a');
    if (!link) return;

    event.preventDefault();

    this.dispatchEvent(new CustomEvent('td:facet-remove', {
      bubbles: true,
      composed: true,
      detail: {
        href: link.href
      }
    }));
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove);
}

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    // Defer noUiSlider work so it doesn't compete with early user interactions.
    const init = () => {
      const slider = this.querySelector('.price_slider');
      const amounts = this.querySelector('.price_slider_amount');
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

      if (!slider || !amounts || !form) return;
      if (typeof noUiSlider === 'undefined') return;

      const args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      };
      const inputEvent = new CustomEvent('input');

      if (slider.classList.contains('noUi-target')) {
        slider.noUiSlider.destroy();
      }
      noUiSlider.create(slider, args);

      slider.noUiSlider.on('update', function(values) {
        amounts.querySelector('.field__input_min').value = values[0];
        amounts.querySelector('.field__input_max').value = values[1];
      });
      slider.noUiSlider.on('change', function(values) {
        form.querySelector('form').dispatchEvent(inputEvent);
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init, { timeout: 1500 });
    } else {
      setTimeout(init, 0);
    }
  }
}
if (!customElements.get('price-slider')) {
  customElements.define('price-slider', PriceSlider);
}

window.addEventListener('load', () => {
  new FacetsToggle();
});